import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchRecentSnapshotsForScope } from '../services/leaderboardMovementService';
import {
  fetchSingleClubLeaderboard,
  fetchPlayerSessionDatesForIds,
  LEADERBOARD_SCOPES,
} from '../services/clubLeaderboardService';
import {
  buildMovementMapForScope,
  collectPlayerIdsFromRecentSnapshots,
  gameTypeToLadderType,
} from '../utils/leaderboardSnapshotUtils';
import { parseLeaderboardScope } from '../utils/leaderboardPlayerScope';

/**
 * Shared hook for FullLeaderboardPage — fetches scoped leaderboard + movement data.
 *
 * @param {Object} params
 * @param {string} params.clubId
 * @param {string|null|undefined} params.seasonId
 * @param {'mixed'|'levels-male'|'levels-female'|'combined'} params.gameType
 */
export function useClubLeaderboard({ clubId, seasonId, gameType }) {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const scope = parseLeaderboardScope(searchParams.get('scope'));

  const setScope = useCallback(
    (nextScope) => {
      const params = new URLSearchParams(location.search);
      if (nextScope === LEADERBOARD_SCOPES.ACTIVE) {
        params.delete('scope');
      } else {
        params.set('scope', nextScope);
      }
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  const [leaderboard, setLeaderboard] = useState([]);
  const [kpis, setKpis] = useState({ totalPlayers: 0, averageElo: 0 });
  const [snapshotMovement, setSnapshotMovement] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [movementLoading, setMovementLoading] = useState(true);
  const [error, setError] = useState(null);

  const ladderType = gameTypeToLadderType(gameType);

  useEffect(() => {
    if (!clubId || !seasonId || !gameType || !ladderType) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setMovementLoading(true);
      setError(null);

      try {
        const boardResult = await fetchSingleClubLeaderboard({
          clubId,
          seasonId,
          gameType,
          scope,
        });

        if (cancelled) return;

        setLeaderboard(boardResult.leaderboard);
        setKpis(boardResult.kpis);
        setLoading(false);

        const recentSnapshots = await fetchRecentSnapshotsForScope(
          clubId,
          seasonId,
          ladderType,
          scope
        ).catch(() => null);

        if (cancelled) return;

        let sessionDatesByPlayerId = {};
        if (scope === LEADERBOARD_SCOPES.ACTIVE) {
          const snapshotPlayerIds = collectPlayerIdsFromRecentSnapshots([recentSnapshots]);
          if (snapshotPlayerIds.length > 0) {
            sessionDatesByPlayerId = await fetchPlayerSessionDatesForIds(
              snapshotPlayerIds,
              clubId,
              seasonId
            );
          }
        }

        if (cancelled) return;

        setSnapshotMovement(
          buildMovementMapForScope(scope, recentSnapshots, sessionDatesByPlayerId)
        );
        setMovementLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('[useClubLeaderboard]', err);
          setError('Failed to load leaderboard data');
          setLoading(false);
          setMovementLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [clubId, seasonId, gameType, scope, ladderType]);

  return {
    leaderboard,
    kpis,
    snapshotMovement,
    loading,
    movementLoading,
    error,
    scope,
    setScope,
  };
}
